import { Inbox } from 'lucide-react';
import { problemText } from '@/shared/api/problemText';
import { useApiProblem } from '@/shared/api/useApiProblem';
import { Button } from '@/shared/components';

/**
 * Die Ebene der Unterhaltung ohne Unterhaltung: nichts gewählt, Abruf läuft, gibt es nicht mehr,
 * Abruf gescheitert. Ein „lädt …“, das nach einem Fehler stehen bliebe, wäre eine Falschaussage;
 * „gibt es nicht mehr“ (404) bietet kein „Erneut laden“, weil derselbe Versuch dasselbe brächte.
 * Der Rückweg erscheint nur am Handy — dort ist diese Fläche die einzige Ebene.
 */
export function EmptyConversation({
  loading,
  paused = false,
  error,
  onRetry,
  onBack,
}: {
  loading: boolean;
  /** Gewählt, aber ohne Verbindung noch nicht geladen. */
  paused?: boolean;
  error: unknown;
  onRetry?: () => void;
  onBack: (() => void) | null;
}) {
  const problem = useApiProblem(error ?? null);
  const missing = problem?.status === 404;
  const failed = Boolean(error) && !missing;
  const title = missing
    ? 'Diese Unterhaltung gibt es nicht mehr.'
    : failed
      ? problemText(problem, 'Die Unterhaltung konnte nicht geladen werden.')
      : paused
        ? 'Keine Verbindung.'
        : loading
          ? 'Unterhaltung wird geladen …'
          : 'Unterhaltung auswählen';
  const hint = missing
    ? 'Sie wurde entfernt oder gehört nicht zu diesem Workspace.'
    : failed
      ? 'Wähle eine andere oder versuche es erneut.'
      : paused
        ? 'Die Unterhaltung lädt, sobald die Verbindung zurück ist.'
        : loading
          ? 'Verlauf und Kontext erscheinen, sobald sie da ist.'
          : 'Wähle eine Unterhaltung, um Verlauf und Kontext zu sehen.';

  return (
    <div className="text-ink-faint flex h-full flex-col items-center justify-center gap-2 px-6 text-center">
      <Inbox size={30} strokeWidth={1.5} aria-hidden />
      <p
        role={failed || missing ? 'alert' : undefined}
        className={failed ? 'text-sealwax text-sm font-semibold' : 'text-ink text-sm font-semibold'}
      >
        {title}
      </p>
      <p className="text-ink-soft text-xs">{hint}</p>
      <div className="mt-2 flex flex-wrap justify-center gap-2">
        {failed && onRetry && (
          <Button type="button" variant="secondary" className="min-h-11" onClick={onRetry}>
            Erneut laden
          </Button>
        )}
        {onBack && (
          <Button
            type="button"
            variant="ghost"
            className="border-line min-h-11 border"
            onClick={onBack}
          >
            Zurück zur Liste
          </Button>
        )}
      </div>
    </div>
  );
}
